import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, CircleAlert, Clock3, XCircle } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { formatDisplayValue } from "@/lib/adminFormatters";
import { formatCurrency } from "@/lib/currency";
import { getApplicationFeeYocoPaymentResult, getErrorMessage } from "@/lib/api";


const STATUS_VIEW = {
  success: {
    icon: CheckCircle2,
    accent: "text-emerald-700",
    surface: "border-emerald-200 bg-emerald-50/70",
    title: "Payment submitted",
    body: "Your return from Yoco was received. We are now waiting for secure verification or admin review before confirming your application fee.",
  },
  cancelled: {
    icon: XCircle,
    accent: "text-slate-700",
    surface: "border-slate-200 bg-slate-50",
    title: "Payment cancelled",
    body: "Your Yoco checkout was cancelled before completion. Your application fee has not been confirmed.",
  },
  failed: {
    icon: CircleAlert,
    accent: "text-rose-700",
    surface: "border-rose-200 bg-rose-50/70",
    title: "Payment not completed",
    body: "Your Yoco checkout did not complete successfully. No payment has been confirmed.",
  },
  processing: {
    icon: Clock3,
    accent: "text-amber-700",
    surface: "border-amber-200 bg-amber-50/70",
    title: "Payment processing",
    body: "Your payment return is being processed. Please allow a moment for the latest status to appear.",
  },
};


const formatInternalStatus = (payment) => {
  if (!payment) return "Awaiting update";
  if (payment.review_needed) return "Awaiting review";
  if (payment.internal_status === "paid_confirmed") return "Paid confirmed";
  if (payment.internal_status === "payment_under_review") return "Payment under review";
  if (payment.provider_status === "failed") return "Payment failed";
  return "Awaiting payment";
};


export default function YocoPaymentResultPage({ publicConfig }) {
  const [searchParams] = useSearchParams();
  const { session, sessionLoading } = useAuth();
  const paymentId = searchParams.get("payment_id") || "";
  const requestedStatus = searchParams.get("status") || "processing";
  const safeStatusKey = STATUS_VIEW[requestedStatus] ? requestedStatus : "processing";
  const [state, setState] = useState({ loading: Boolean(paymentId), error: "", payment: null });

  useEffect(() => {
    if (!paymentId) {
      setState({ loading: false, error: "", payment: null });
      return;
    }

    if (sessionLoading) {
      setState((current) => ({ ...current, loading: true }));
      return;
    }

    if (session?.role !== "student") {
      setState({ loading: false, error: "", payment: null });
      return;
    }

    let active = true;
    const loadPaymentResult = async () => {
      setState({ loading: true, error: "", payment: null });
      try {
        const response = await getApplicationFeeYocoPaymentResult(paymentId);
        if (!active) return;
        setState({ loading: false, error: "", payment: response.data.payment || null });
      } catch (error) {
        if (!active) return;
        setState({ loading: false, error: getErrorMessage(error, "We could not load the latest Yoco payment result."), payment: null });
      }
    };

    loadPaymentResult();
    return () => {
      active = false;
    };
  }, [paymentId, session?.role, sessionLoading]);

  const view = useMemo(() => {
    if (state.payment?.internal_status === "paid_confirmed") {
      return {
        ...STATUS_VIEW.success,
        title: "Application fee confirmed",
        body: "Your Yoco payment has been verified and the application fee is confirmed.",
      };
    }
    if (state.payment?.review_needed) {
      return {
        ...STATUS_VIEW.processing,
        title: "Verification or review still pending",
        body: state.payment.review_reason_message || "Your Yoco payment record is visible, but it still requires verification or admin review before it can be confirmed.",
      };
    }
    if (state.payment?.provider_status === "failed") {
      return STATUS_VIEW.failed;
    }
    return STATUS_VIEW[safeStatusKey];
  }, [safeStatusKey, state.payment]);

  const Icon = view.icon;
  const supportEmail = publicConfig?.support_email || "support@zallywoodunisa.org";

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 py-10" data-testid="yoco-payment-result-page">
      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="yoco-payment-result-card">
        <CardHeader>
          <CardTitle className="text-4xl font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="yoco-payment-result-title">
            Yoco payment result
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className={`rounded-[1.35rem] border p-5 ${view.surface}`} data-testid="yoco-payment-result-summary-card">
            <div className="flex items-start gap-4">
              <div className={`rounded-full bg-white/80 p-3 ${view.accent}`} data-testid="yoco-payment-result-icon-wrap">
                <Icon className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <p className={`text-lg font-semibold ${view.accent}`} data-testid="yoco-payment-result-status-title">{view.title}</p>
                <p className="text-sm leading-7 text-slate-700" data-testid="yoco-payment-result-status-body">{view.body}</p>
              </div>
            </div>
          </div>

          {state.loading ? <p className="text-sm text-slate-600" data-testid="yoco-payment-result-loading-text">Loading the latest payment status...</p> : null}

          {state.error ? (
            <div className="rounded-[1.15rem] border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-7 text-rose-700" data-testid="yoco-payment-result-error-message">
              {state.error}
            </div>
          ) : null}

          {state.payment ? (
            <div className="grid gap-4 md:grid-cols-2" data-testid="yoco-payment-result-details-grid">
              <div className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid="yoco-payment-result-record-id-card">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Payment record</p>
                <p className="mt-2 break-words text-sm leading-7 text-slate-800" data-testid="yoco-payment-result-record-id-value">{state.payment.id}</p>
              </div>
              <div className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid="yoco-payment-result-amount-card">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Amount</p>
                <p className="mt-2 text-sm leading-7 text-slate-800" data-testid="yoco-payment-result-amount-value">{formatCurrency(state.payment.amount)} {state.payment.currency}</p>
              </div>
              <div className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid="yoco-payment-result-internal-status-card">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Current status</p>
                <p className="mt-2 text-sm leading-7 text-slate-800" data-testid="yoco-payment-result-internal-status-value">{formatInternalStatus(state.payment)}</p>
              </div>
              <div className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid="yoco-payment-result-provider-status-card">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Provider status</p>
                <p className="mt-2 text-sm leading-7 text-slate-800" data-testid="yoco-payment-result-provider-status-value">{state.payment.provider_status || "Awaiting webhook update"}</p>
              </div>
              <div className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid="yoco-payment-result-updated-card">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Last updated</p>
                <p className="mt-2 text-sm leading-7 text-slate-800" data-testid="yoco-payment-result-updated-value">{state.payment.updated_at ? formatDisplayValue(state.payment.updated_at) : "—"}</p>
              </div>
              <div className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid="yoco-payment-result-webhook-card">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Webhook confirmed</p>
                <p className="mt-2 text-sm leading-7 text-slate-800" data-testid="yoco-payment-result-webhook-value">{state.payment.webhook_confirmed_at ? formatDisplayValue(state.payment.webhook_confirmed_at) : "Not yet confirmed"}</p>
              </div>
            </div>
          ) : null}

          {!sessionLoading && session?.role !== "student" ? (
            <div className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] px-4 py-3 text-sm leading-7 text-slate-700" data-testid="yoco-payment-result-login-note">
              Sign in to the student portal if you want to view the latest payment record details for this return.
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3" data-testid="yoco-payment-result-actions-row">
            {session?.role === "student" ? (
              <Button asChild className="rounded-full bg-[#1f2d3d] hover:bg-[#15212d]" data-testid="yoco-payment-result-back-to-portal-button">
                <Link to="/student">Back to student portal</Link>
              </Button>
            ) : (
              <Button asChild className="rounded-full bg-[#1f2d3d] hover:bg-[#15212d]" data-testid="yoco-payment-result-login-button">
                <Link to="/login">Go to student login</Link>
              </Button>
            )}
            <Button asChild variant="outline" className="rounded-full" data-testid="yoco-payment-result-support-button">
              <a href={`mailto:${supportEmail}`}>Contact support</a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}